import { useEffect, useRef, useState } from "react";

import { SearchField } from "@/components/ui/search-field";
import {
  CATALOG_SEARCH_TERM_MAX_LENGTH,
  catalogSearchTermLength,
  normalizeCatalogSearchTerm,
} from "@/lib/catalog-list-url-state";

// The Item Catalog's search box.
//
// **The one stateful control on this page**, and the pause is why: the URL is
// the page's only state, but a URL rewritten per keystroke would be a request
// per keystroke and a history entry per keystroke. So the typed text lives here
// for 400ms and the *term* lives in the URL, which keeps every search
// deep-linkable and back-button-safe without putting `?search=f`, `?search=fa`,
// `?search=fau` in the user's way out.
//
// 400ms is the donor's own number, from both of its search boxes
// (`items-list-page.tsx`: `useDebounce(searchFieldValue, 400)`;
// `SearchInput.tsx`: "sync the search filter after a 400ms pause").
//
// Clearing goes through the same pause as typing, which is this page's donor's
// behaviour: `ItemsSearchbox.handleClearSearch` calls `setValue(fieldName, '')`
// and that value reaches the URL through the very same
// `useDebounce(searchFieldValue, 400)`. Modsy's *island* box is the one that
// clears straight away (`SearchInput.tsx`), and clearing at once here would be
// the better UX — but it is a deviation from the screen being ported, so it
// stays a proposal on the PR's decision menu rather than something this session
// decides (`migration-guidelines.md`, "Parity is the North Star").
//
// `SearchField` still reports the two as separate events; this screen is simply
// choosing to treat them the same.

/** The donor's pause, exported so a test can wait exactly it out. */
export const CATALOG_SEARCH_DEBOUNCE_MS = 400;

/**
 * "No report of ours is waiting to come back down."
 *
 * A symbol rather than `undefined`, because `undefined` is itself a term the
 * box reports — an emptied box — and the two must stay tellable apart. Reading
 * them as one makes a Back out of a real term look like the box's own echo.
 */
const NOTHING_REPORTED = Symbol("nothing reported");

/**
 * `value`, shortened until the term it holds is one Python would accept.
 *
 * The box stops the user at the cap rather than letting a term through that
 * Python refuses outright — that refusal reaches the browser as the generic
 * scrubbed message, which says nothing a user could act on.
 *
 * This is the `maxLength` attribute's job, done with the right measure.
 * `maxLength` counts UTF-16 units and counts the spaces around a term, so it
 * refuses input both contracts accept: 200 characters typed with a space in
 * front, or 200 astral characters, which it sees as 400. Trimming from the end
 * keeps the attribute's actual behaviour — a long paste arrives shortened
 * rather than rejected whole.
 */
function cappedTerm(value: string) {
  const characters = [...value];
  while (catalogSearchTermLength(characters.join("")) > CATALOG_SEARCH_TERM_MAX_LENGTH) {
    characters.pop();
  }
  return characters.join("");
}
type Reported = string | undefined | typeof NOTHING_REPORTED;

interface CatalogSearchBoxProps {
  /** The term the URL carries, already normalised. `undefined` is no search. */
  term: string | undefined;
  onTermChange: (term: string | undefined) => void;
  className?: string;
}

export function CatalogSearchBox({ term, onTermChange, className }: CatalogSearchBoxProps) {
  const [draft, setDraft] = useState(term ?? "");
  const [lastTerm, setLastTerm] = useState(term);
  const [echo, setEcho] = useState<Reported>(NOTHING_REPORTED);

  const accept = (next: string) => setDraft(cappedTerm(next));

  // A press of *clear* skips the pause and reports at once — an owner-ruled
  // deviation from this page's donor, which sends its clear back through the
  // same `useDebounce(..., 400)` as typing (`items-searchbox.tsx:58` ->
  // `items-list-page.tsx:141`). Nothing is dropped by going sooner: the same
  // results arrive, four-tenths of a second earlier, and modsy's other search
  // box already behaves this way ("sync the search filter straight away",
  // `SearchInput.tsx:55-58`). Ruled on #283, D2, 2026-08-31.
  //
  // Nothing is reported when there is no term yet: the user is clearing text
  // they typed but never searched for, and a navigation there would be a
  // history entry to nowhere.
  const clear = () => {
    setDraft("");
    if (term === undefined) return;
    setEcho(undefined);
    onTermChange(undefined);
  };

  // The pause, as an effect over the typed text rather than a timer armed by
  // the keystroke handler. The cleanup is what makes it correct: React runs it
  // before every re-arm and on unmount, so *every* way a pending report can be
  // superseded is handled by construction rather than by remembering to cancel
  // at each of them.
  //
  // The one that had been missed: a term arriving from outside mid-pause — Back
  // pressed inside the 400ms. The draft is resynced below, this effect re-runs,
  // and the abandoned term's pause is cleaned up instead of firing four-tenths
  // of a second later and pushing it straight back into the URL.
  //
  // `next === term` is the exit for "already showing this": without it, opening
  // on a deep link would report the term it arrived with straight back to the
  // page, navigating to where it already is.
  const report = useRef(onTermChange);
  useEffect(() => {
    // A plain assignment in an effect, not a render-phase write: it keeps the
    // pause below off `onTermChange`'s identity, which the page rebuilds on
    // every render. Depending on it directly would restart the 400ms whenever
    // anything else on the page re-rendered, and a busy page would never search
    // at all.
    report.current = onTermChange;
  });

  useEffect(() => {
    const next = normalizeCatalogSearchTerm(draft);
    if (next === term) return;

    const pause = setTimeout(() => {
      // Recorded as this box's own before the page hears about it, so the
      // resync below can tell the echo apart from a real navigation.
      setEcho(next);
      report.current(next);
    }, CATALOG_SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(pause);
  }, [draft, term]);

  // The term moved somewhere the box is not showing: a Back, a deep link, or
  // the applied-filter chip's own remove button. The box follows it.
  //
  // Adjusted **during render** rather than in an effect — React's own
  // "adjusting state when a prop changes" pattern, which re-renders this
  // component before anything is painted instead of showing the stale value for
  // a frame and then correcting it.
  //
  // Except when the term is the box's own report coming back down. Resyncing on
  // that one would reach in and delete whatever the user has typed since — the
  // keystrokes that land while the first word's search is in flight. The echo is
  // consumed as it is matched, so a later navigation *back* to that same term is
  // an outside change like any other.
  if (term !== lastTerm) {
    setLastTerm(term);
    if (term !== echo) setDraft(term ?? "");
    if (echo !== NOTHING_REPORTED) setEcho(NOTHING_REPORTED);
  }

  return (
    <SearchField
      value={draft}
      onChange={accept}
      onClear={clear}
      // The donor's placeholder, verbatim — it is the whole of what a user
      // reads on this control, since the box carries no label.
      placeholder="Search by item name, model #, or keyword"
      aria-label="Search items"
      className={className}
      data-testid="catalog-search-box"
    />
  );
}
